'use client';

import { NAVIGATION_CONSTANTS } from '@fdc/constants/navigation.constants';
import { useEffect, useRef } from 'react';

export interface IFrameContainerProps {
  src: string;
  onMessage?: (event: MessageEvent) => void;
  iframeProps?: React.IframeHTMLAttributes<HTMLIFrameElement>;
  onRouteChange?: (url: string) => void;
  onAuthRedirect?: (redirectUrl: string) => void;
  extraHeight?: number;
}

const MINIMUM_IFRAME_HEIGHT = 1000;

export function IframeContainer({
  src,
  onMessage,
  iframeProps,
  onRouteChange,
  onAuthRedirect,
  extraHeight,
}: IFrameContainerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onMessageRef = useRef(onMessage);
  const onRouteChangeRef = useRef(onRouteChange);
  const onAuthRedirectRef = useRef(onAuthRedirect);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onRouteChangeRef.current = onRouteChange;
    onAuthRedirectRef.current = onAuthRedirect;
  }, [onMessage, onRouteChange, onAuthRedirect]);

  useEffect(() => {
    const getScrollHeight = (scrollHeight?: number) =>
      Math.max(scrollHeight ?? 0, MINIMUM_IFRAME_HEIGHT) + (extraHeight ?? 0);

    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (iframe) {
        switch (event.data?.type) {
          case 'IFRAME_HEIGHT':
            iframe.style.height = `${getScrollHeight(event.data.payload)}px`;
            break;
          case 'ROUTER_CHANGE_START':
            iframe.contentWindow?.scrollTo(0, 0);
            break;
          case 'AUTH_REDIRECT': {
            const redirectUrl =
              new URL(
                event.data.payload?.url,
                window.location.origin
              ).searchParams.get('redirectUrl') ?? '';
            if (onAuthRedirectRef.current) {
              onAuthRedirectRef.current(redirectUrl);
            } else {
              window.location.href = `${NAVIGATION_CONSTANTS.LOGIN_PAGE}?returnTo=${encodeURIComponent(redirectUrl)}`;
            }
            break;
          }
          case 'NAVIGATE': {
            const { url: navigateUrl, historyPushOnly } =
              event.data?.payload ?? {};
            if (navigateUrl) {
              const { pathname, search, hash } = new URL(
                navigateUrl,
                window.location.origin
              );
              const path = `${pathname}${search}${hash}`;
              if (historyPushOnly) {
                onRouteChangeRef.current?.(path);
              } else {
                window.location.href = path;
              }
            }
            break;
          }
          case 'ROUTER_CHANGE_END': {
            const { scrollHeight } = event.data?.payload ?? {};
            if (scrollHeight) {
              iframe.style.height = `${getScrollHeight(scrollHeight)}px`;
            }
            break;
          }

          default:
            onMessageRef.current?.(event);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [extraHeight]);

  return (
    <div>
      <iframe
        ref={iframeRef}
        src={src}
        style={{
          width: '100%',
          height: `${MINIMUM_IFRAME_HEIGHT}px`,
          border: 'none',
          overflow: 'hidden',
        }}
        {...iframeProps}
      />
    </div>
  );
}
